<template>
  <div class="card trend-card p-x2">
    <div class="t-lg bold m-bx1">{{ title }}</div>
    <div v-if="loading" class="trend-loading">
      <donut-loading-dots />
    </div>
    <div v-else-if="sortedPoints.length && hasAnyData" class="f f-h">
      <!-- Icon strip: sentiment faces (1–5) or participation bars (0–4), aligned with grid lines -->
      <div class="trend-axis-icons m-rx1">
        <cdn-svg
          v-for="(iconPath, idx) in axisIconPaths"
          :key="iconPath"
          :path="iconPath"
          :version="0"
          class="h-x3 w-x3 trend-axis-icon"
          :style="{ top: `${gridLineTops[idx]}px` }"
        />
      </div>
      <div class="trend-chart-container f-grow">
        <Line
          :id="`trend-chart-${type}`"
          :data="chartData"
          :options="chartOptions"
          class="trend-chart-canvas"
        />
      </div>
    </div>
    <div v-else class="trend-empty">
      No data available
    </div>
  </div>
</template>

<script setup>
/**
 * Trend chart for Team Pulse: displays Sentiment or Participation scores over time.
 * Uses Chart.js for the line graph with a custom icon strip on the left (faces or bars).
 */
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';

// Same pattern as stacked-bar-chart.vue and pie-chart.vue: register only what we need
ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
);

const props = defineProps({
  title: { type: String, required: true },
  points: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  scoreKey: { type: String, required: true }, // e.g. 'sentimentScore' or 'engagementScore'
  type: {
    type: String,
    required: true,
    validator: (value) => ['sentiment', 'engagement'].includes(value),
  },
});

// All points sorted by date (oldest first). Include points with null scores so we can show breaks.
const sortedPoints = computed(() => props.points
  .slice()
  .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0)));

// For "no data" check: need at least one point with a finite score
const hasAnyData = computed(() => sortedPoints.value.some((p) => Number.isFinite(p?.[props.scoreKey])));

// Points use week-start dates; we display end-of-week (e.g. "Aug 31") on the x-axis
const formatEndOfWeek = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 6);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
};

// Chart.js data: labels = x-axis dates, data = scores (0–100)
// null = break in line (no data for that week); sentiment and participation both use null for breaks
const chartData = computed(() => {
  const pts = sortedPoints.value;
  const labels = pts.map((p) => formatEndOfWeek(p.date));
  const data = pts.map((p) => {
    const score = p?.[props.scoreKey];
    return Number.isFinite(score) ? Math.max(0, Math.min(100, Number(score))) : null;
  });
  // Single point: duplicate so we draw a horizontal line instead of an invisible dot
  if (pts.length === 1 && data[0] != null) {
    labels.push(labels[0]);
    data.push(data[0]);
  }
  return {
    labels,
    datasets: [
      {
        label: props.title,
        data,
        borderColor: '#52aef0',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.4, // smooth curve
        spanGaps: false, // null values create a break in the line
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };
});

// Pixel positions for icons so they align with grid lines at 0, 25, 50, 75, 100
const gridLineTops = [20, 50, 80, 110, 140];

// CDN paths for icons: sentiment faces (5=happy → 1=sad) or participation bars (4=high → 0=low)
const axisIconPaths = computed(() => {
  if (props.type === 'sentiment') {
    return [
      'nucleo/sentiment-face-5',
      'nucleo/sentiment-face-4',
      'nucleo/sentiment-face-3',
      'nucleo/sentiment-face-2',
      'nucleo/sentiment-face-1',
    ];
  }
  return [
    'nucleo/meter-bars-4',
    'nucleo/meter-bars-3',
    'nucleo/meter-bars-2',
    'nucleo/meter-bars-1',
    'nucleo/meter-bars-0',
  ];
});

// Chart.js options: no legend, no y-axis labels, horizontal grid lines, end-of-week x labels
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: { top: 10, bottom: 30, left: 0, right: 10 },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        maxRotation: 0,
        font: { size: 12 },
        color: '#645E71',
        maxTicksLimit: 6,
      },
    },
    y: {
      min: 0,
      max: 100,
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.08)',
        drawTicks: false,
      },
      border: { display: false },
      ticks: { display: false, stepSize: 25 }, // 5 grid lines; labels hidden (we use icons)
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
    // chartjs-plugin-datalabels is registered globally (by stacked-bar-chart), so we must turn it off
    datalabels: { display: () => false },
  },
}));
</script>

<style scoped lang="scss">
@import '../../styles/donut';

.trend-card {
  min-height: 260px;
}

/* Icon strip: same height as chart; icons are absolutely positioned within it */
.trend-axis-icons {
  width: 24px;
  height: 200px;
  position: relative;
}

.trend-axis-icon {
  position: absolute;
  left: 0;
  transform: translateY(-50%); /* center icon vertically on grid line */
}

.trend-chart-container {
  height: 200px;
  min-width: 0;
}

.trend-chart-canvas {
  width: 100% !important;
  height: 200px !important;
}

.trend-loading,
.trend-empty {
  height: 200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trend-empty {
  color: $gray;
  font-size: 14px;
}
</style>
